import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import useDefaultMapConfig from '../hooks/useDefaultMapConfig';
import { createClusterMarker } from '../utils/clusterMarkerIcon';

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDfJ-ekd6yt6eT6G2lkRDE4-R5rpxq1jLU';
const GOOGLE_MAPS_LIBRARIES = [];
const DEFAULT_CENTER = { lat: -37.7354797, lng: 144.9727395 }; // Center of Murray Road segments, Coburg North VIC

// Helper function to format time for display
function formatTimeForDisplay(
  timeString,
  timeUnknown = false,
  isDateRange = false,
  rangeStart = null,
  rangeEnd = null,
) {
  // Handle date ranges first
  if (isDateRange === true || isDateRange === 'true') {
    if (rangeStart && rangeEnd) {
      try {
        const startDate = new Date(rangeStart);
        const endDate = new Date(rangeEnd);
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const options = {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: 'Australia/Melbourne',
          };
          return `Between ${startDate.toLocaleDateString('en-AU', options)} - ${endDate.toLocaleDateString('en-AU', options)}`;
        }
      } catch {
        // Fall through to default handling
      }
    }
    return 'Date range (unknown)';
  }

  if (!timeString) return '—';

  try {
    const date = new Date(timeString);
    if (isNaN(date.getTime())) return timeString;

    // If time is unknown, show date-only format with tilde prefix
    if (timeUnknown === true || timeUnknown === 'true') {
      const options = {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'Australia/Melbourne',
      };
      return `~${date.toLocaleDateString('en-AU', options)}`;
    }

    // Format as "12 Jul 2025, 2:30 PM" in Melbourne timezone
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Australia/Melbourne',
    };

    return date.toLocaleDateString('en-AU', options);
  } catch (error) {
    return timeString;
  }
}

function IncidentClusterMap({
  reports,
  typeFilter = 'all',
  dateRange = { from: '', to: '' },
  includePending = false,
}) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    version: 'weekly',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const { mapRestrictions } = useDefaultMapConfig();

  const [selectedReport, setSelectedReport] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const clustererRef = useRef(null);
  const markersRef = useRef([]);
  const isCreatingClustererRef = useRef(false);

  // Filter reports based on current filters
  const filteredReports = useMemo(() => {
    return reports.filter(r => {
      if (!includePending && !r.isApproved) return false;
      if (typeFilter !== 'all' && r.incidentType !== typeFilter) return false;

      // Handle date range filtering for both single dates and date ranges
      if (dateRange.from) {
        if (r.isDateRange && r.incidentDateRangeStart) {
          if (new Date(r.incidentDateRangeStart) < new Date(dateRange.from)) return false;
        } else if (r.estimatedIncidentTime) {
          if (new Date(r.estimatedIncidentTime) < new Date(dateRange.from)) return false;
        }
      }

      if (dateRange.to) {
        if (r.isDateRange && r.incidentDateRangeEnd) {
          if (new Date(r.incidentDateRangeEnd) > new Date(dateRange.to)) return false;
        } else if (r.estimatedIncidentTime) {
          if (new Date(r.estimatedIncidentTime) > new Date(dateRange.to)) return false;
        }
      }

      return (
        r.coordinates &&
        ((typeof r.coordinates === 'string' && r.coordinates.trim() !== '') ||
          (typeof r.coordinates === 'object' && r.coordinates.lat && r.coordinates.lng))
      );
    });
  }, [reports, typeFilter, dateRange, includePending]);

  // Parse coordinates and create markers data
  const markersData = useMemo(() => {
    return filteredReports
      .map(report => {
        let coords;
        if (typeof report.coordinates === 'string') {
          coords = report.coordinates.trim();
        } else if (
          typeof report.coordinates === 'object' &&
          report.coordinates.lat &&
          report.coordinates.lng
        ) {
          coords = `${report.coordinates.lat}, ${report.coordinates.lng}`;
        } else {
          return null; // Skip invalid coordinate formats
        }

        const match = coords.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
        if (!match) return null;

        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);

        if (isNaN(lat) || isNaN(lng)) return null;

        return {
          ...report,
          position: { lat, lng },
          parsedCoords: { lat, lng },
        };
      })
      .filter(Boolean);
  }, [filteredReports]);

  // Get color for incident type
  const getIncidentTypeColor = incidentType => {
    const typeColors = {
      'car-collision': '#ef4444',
      'car-pedestrian': '#f59e0b',
      'car-bicycle': '#3b82f6',
      'car-motorcycle': '#f43f5e',
      'car-animal': '#a16207',
      'car-property': '#eab308',
      'car-rollover': '#8b5cf6',
      'car-fire': '#b91c1c',
      'motorcycle-collision': '#10b981',
      'motorcycle-car': '#f472b6',
      'truck-collision': '#6366f1',
      'bicycle-collision': '#06b6d4',
      'pedestrian-collision': '#84cc16',
      'near-miss': '#f97316',
      'dangerous-driving': '#dc2626',
    };
    return typeColors[incidentType] || '#6b7280';
  };

  // Create custom cluster renderer
  const createClusterRenderer = () => {
    return {
      render: ({ count, position }) => {
        const color = count < 5 ? '#3b82f6' : count < 15 ? '#f59e0b' : '#ef4444';
        const size = count < 5 ? 40 : count < 15 ? 50 : 60;

        const svg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 40 40">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.25)"/>
              </filter>
            </defs>
            <circle cx="20" cy="20" r="18" fill="${color}" filter="url(#shadow)"/>
            <circle cx="20" cy="20" r="14" fill="rgba(255,255,255,0.15)"/>
          </svg>
        `;

        return new window.google.maps.Marker({
          position,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            scaledSize: new window.google.maps.Size(size, size),
          },
          label: {
            text: String(count),
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: '700',
          },
          zIndex: window.google.maps.Marker.MAX_ZINDEX + count,
        });
      },
    };
  };

  // Stable clustering function - no dependencies to prevent recreation
  const createClusterer = useCallback((map, markers) => {
    if (!map || !markers.length) return null;

    try {
      console.log('🎯 Creating MarkerClusterer with', markers.length, 'markers');
      const clusterer = new MarkerClusterer({
        map,
        markers,
        renderer: {
          render: ({ count, position }) => createClusterMarker(count, position),
        },
      });

      // Add click listener for clusters
      clusterer.addListener('clusterclick', event => {
        const cluster = event.cluster;
        const clusterMarkers = cluster.markers;
        if (clusterMarkers.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          clusterMarkers.forEach(marker => bounds.extend(marker.getPosition()));
          map.fitBounds(bounds);
        }
      });

      console.log('✅ MarkerClusterer created successfully');
      return clusterer;
    } catch (error) {
      console.error('❌ Error creating MarkerClusterer:', error);
      return null;
    }
  }, []); // Empty dependency array for stability

  // Initialize markers and clustering
  useEffect(() => {
    if (
      !isLoaded ||
      !mapRef ||
      !markersData.length ||
      !window.google ||
      isCreatingClustererRef.current
    )
      return;

    // Prevent multiple simultaneous creations
    isCreatingClustererRef.current = true;

    // Clear existing markers and clusterer
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Create markers
    const markers = markersData.map(report => {
      const marker = new window.google.maps.Marker({
        position: report.parsedCoords,
        map: mapRef,
        title: `${report.incidentType} - ${report.location}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: getIncidentTypeColor(report.incidentType),
          fillOpacity: 0.8,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          scale: 8,
        },
      });

      // Add click listener for info window
      marker.addListener('click', () => {
        setSelectedReport(report);
      });

      markersRef.current.push(marker);
      return marker;
    });

    // Create clusterer if markers exist
    if (markers.length > 0) {
      const newClusterer = createClusterer(mapRef, markers);
      if (newClusterer) {
        clustererRef.current = newClusterer;
      } else {
        // Fallback: show individual markers
        markers.forEach(marker => marker.setMap(mapRef));
      }
    }

    // Fit bounds to show all markers
    if (markers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(marker => bounds.extend(marker.getPosition()));
      mapRef.fitBounds(bounds);

      // Add some padding to bounds
      setTimeout(() => {
        mapRef.fitBounds(bounds, 50);
      }, 100);
    }

    // Reset creation flag
    isCreatingClustererRef.current = false;

    return () => {
      isCreatingClustererRef.current = false;
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
    };
  }, [isLoaded, mapRef, markersData]); // Removed createClusterer from dependencies

  if (!isLoaded) {
    return (
      <div className="w-full h-96 bg-gray-100 rounded-xl flex items-center justify-center">
        <div className="text-gray-500">Loading map...</div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-xl border border-gray-200">
      {/* Map Container */}
      <div className="w-full h-96 overflow-hidden">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={DEFAULT_CENTER}
          zoom={13}
          onLoad={map => setMapRef(map)}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            gestureHandling: 'cooperative',
            zoomControl: true,
            mapTypeId: 'roadmap',
            ...(mapRestrictions?.enabled && {
              restriction: {
                latLngBounds: mapRestrictions.bounds,
                strictBounds: mapRestrictions.strictBounds,
              },
            }),
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
              {
                featureType: 'poi.business',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
              {
                featureType: 'transit',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
            ],
          }}
        >
          {/* Markers are handled by MarkerClusterer */}
        </GoogleMap>

        {/* Info Window */}
        {selectedReport && (
          <InfoWindow
            position={selectedReport.parsedCoords}
            onCloseClick={() => setSelectedReport(null)}
          >
            <div className="max-w-xs p-2">
              <h3 className="font-semibold text-gray-900 mb-1">{selectedReport.incidentType}</h3>
              <p className="text-sm text-gray-600 mb-2">{selectedReport.location}</p>
              <p className="text-xs text-gray-500 mb-2">
                {formatTimeForDisplay(
                  selectedReport.estimatedIncidentTime,
                  selectedReport.timeUnknown,
                  selectedReport.isDateRange,
                  selectedReport.incidentDateRangeStart,
                  selectedReport.incidentDateRangeEnd,
                )}
              </p>
              {selectedReport.description && (
                <p className="text-xs text-gray-700">
                  {selectedReport.description.length > 100
                    ? `${selectedReport.description.substring(0, 100)}...`
                    : selectedReport.description}
                </p>
              )}
            </div>
          </InfoWindow>
        )}
      </div>

      {/* Legend - Now below the map */}
      <div className="bg-white p-4 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Incident Types */}
          <div>
            <div className="font-semibold mb-2 text-sm">Incident Types</div>
            <div className="space-y-1 text-xs">
              {[
                'car-collision',
                'car-pedestrian',
                'car-bicycle',
                'car-motorcycle',
                'near-miss',
              ].map(type => (
                <div key={type} className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getIncidentTypeColor(type) }}
                  />
                  <span className="capitalize">{type.replace('-', ' ')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Clusters */}
          <div>
            <div className="font-semibold mb-2 text-sm">Clusters</div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">
                  5
                </div>
                <span>Small cluster</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 rounded-full bg-yellow-500 text-white text-xs flex items-center justify-center">
                  15
                </div>
                <span>Medium cluster</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                  25
                </div>
                <span>Large cluster</span>
              </div>
            </div>
          </div>

          {/* Status and Info */}
          <div>
            <div className="font-semibold mb-2 text-sm">Status & Info</div>
            <div className="space-y-1 text-xs text-gray-600">
              <div className="text-green-600">✓ Clustering enabled</div>
              <div>Click clusters to zoom in</div>
              <div>Click markers for details</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IncidentClusterMap;
